import { CommonModule as AngularCommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AppRoutingModule } from '../app-routing.module';
import { GEOID_SERVICE_PROVIDER } from '../global/components/forms/geoid-address/service/geoid.service';
import { UploadModalModule } from '../global/components/forms/upload-modal/upload-modal.module';
import { FileAttachmentModule } from '../global/components/file-attachment/file-attachment.module';

import { GlobalModule } from '../global/global.module';
import { MaterialModule } from '../material.module';
import { UserCardDeleteDialogComponent } from './dialogs/user-card-delete-dialog/user-card-delete-dialog.component';
import { UserCardRequestDetailDialogComponent } from './dialogs/user-card-request-detail-dialog/user-card-request-detail-dialog.component';
import { UserConfirmAccountDialogComponent } from './dialogs/user-confirm-account-dialog/user-confirm-account.component';
import { UserDeleteDialogComponent } from './dialogs/user-delete-dialog/user-delete.component';
import { StepConfirmComponent } from './dialogs/user-family-and-card-dialog/steps/step-confirm/step-confirm.component';
import { StepMembersComponent } from './dialogs/user-family-and-card-dialog/steps/step-members/step-members.component';
import { UserFamilyAndCardDialogComponent } from './dialogs/user-family-and-card-dialog/user-family-and-card-dialog.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { UserTableComponent } from './components/user-table/user-table.component';
import { PermissionGuard } from './guard/permission.guard';
import { ScopeGuard } from './guard/scope.guard';
import { UserCreateComponent } from './pages/user-create/user-create.component';
import { UserRecordsComponent } from './pages/user-records/user-records.component';
import { UserUpdateComponent } from './pages/user-update/user-update.component';
import { USER_SERVICE_PROVIDER } from './service/user.service';
import { StepCardComponent } from './dialogs/user-family-and-card-dialog/steps/step-card/step-card.component';
import { UserCardRequestDetailFormComponent } from './forms/user-card-request-detail-form/user-card-request-detail-form.component';
import { UserFamilyRequestCardComponent } from './dialogs/user-family-and-card-dialog/user-family-request-card/user-family-request-card.component';
import { UserCardDeleteErrorDialogComponent } from './dialogs/user-card-delete-error-dialog/user-card-delete-error-dialog.component';
import { UserRequestDenyDialogComponent } from './dialogs/user-request-deny-dialog/user-request-deny-dialog.component';
import { EmailChangedBannerComponent } from './components/banners/email-changed-banner/email-changed-banner.component';
import { StepConfirmationComponent } from './components/user-cards/steps/confirmation/confirmation.component';
import { AddNewFamilyMemberDialogComponent } from './dialogs/add-new-family-member-dialog/add-new-family-member-dialog.component';
import { AddressCardComponent } from './components/user-cards/address-card/address-card.component';
import { EditAddressDialogComponent } from './dialogs/edit-address-dialog/edit-address-dialog.component';
import { LinkCardDialogComponent } from './dialogs/link-card-dialog/link-card-dialog.component';
import { IncompleteUserRecordComponent } from './dialogs/incomplete-user-record-dialog/incomplete-user-record.component';
import { UpdateCardDialogComponent } from './dialogs/update-card-dialog/update-card-dialog.component';
import { AddFamilyMemberSelectionDialogComponent } from './dialogs/add-family-member-selection-dialog/add-family-member-selection-dialog.component';
import { AddExistingUserDialogComponent } from './dialogs/add-existing-user-dialog/add-existing-user-dialog.component';
import { UserCardComponent } from './components/user-cards/user-cards/user-cards.component';
import { FamilyCardComponent } from './components/user-cards/family-card/family-card.component';
import { FamilyMemberButtonComponent } from './components/user-cards/family-member-button/family-member-button.component';
import { UserCardSelectComponent } from './components/user-cards/form/select/user-card-select.component';
import { UserCardSelectDropdownComponent } from './components/user-cards/form/select/dropdown/user-card-select-dropdown.component';
import { StepInfoComponent } from './components/user-cards/steps/info/info.component';
import { CardComponent } from './components/user-cards/card/card.component';
import { UserSearchResultsDialog } from './dialogs/user-search-results-dialog/user-search-results-dialog.component';
import { CreateCardDialogComponent } from './dialogs/create-card-dialog/create-card-dialog.component';
import { UserCardConfirmDialogComponent } from './dialogs/user-card-confirm-card-dialog/user-card-confirm-card-dialog.component';
import { IncompleteProfileComponent } from './dialogs/incomplete-profile-dialog/incomplete-profile.component';
import { BcitiStepInfoComponent } from './components/user-cards/steps/bciti-info/bciti-info.component';
import { UserFamilyComponent } from './components/user-family/user-family/user-family.component';
import { FamilyMemberComponent } from './components/user-family/family-member/family-member.component';
import { UserFamilySelectComponent } from './components/user-family/user-family-select/user-family-select.component';
import { UserFamilySelectDropdownComponent } from './components/user-family/user-family-select-dropdown/user-family-select-dropdown.component';
import { AddFamilyMemberDialogComponent } from './dialogs/add-family-member-dialog/add-family-member-dialog.component';
import { RemoveFamilyConfirmDialogComponent } from './dialogs/remove-family-confirm-dialog/remove-family-confirm-dialog.component';
import { ForceAcceptFamilyLinkDialogComponent } from './dialogs/force-accept-family-link-dialog/force-accept-family-link-dialog.component';
import { EditFamilyLinkDialogComponent } from './dialogs/edit-family-link-dialog/edit-family-link-dialog.component';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { UserSubscriptionsComponent } from './components/user-subscriptions/user-subscriptions/user-subscriptions.component';
import { SubscriptionComponent } from './components/user-subscriptions/subscription/subscription.component';
import { SubscriptionManagementDialogComponent } from './dialogs/user-subscription-management-dialog/user-subscription-management-dialog.component';
import { SubscriptionPaymentDialogComponent } from './dialogs/user-subscripion-payment-dialog/user-subscription-payment-dialog.component';
import { UserReceiptSummaryComponent } from './components/user-receipts/receipt-summary/user-receipt-summary.component';
import { UserReceiptDetailDialogComponent } from './dialogs/user-receipt-detail-dialog/user-receipt-detail-dialog.component';
import { UserReceiptsComponent } from './components/user-receipts/user-receipts/user-receipts.component';
import { UserRequestComponent } from './components/user-requests/user-requests/user-requests.component';
import { UserRequestDetailComponent } from './components/user-requests/detail/user-request-detail.omponent';
import { UserCardProfilePictureComponent } from './forms/user-cards-profile-picture/user-cards-profile-picture.component';
import { VehiculeModalComponent } from './components/user-vehicle/component/vehicule-modal/vehicule-modal.component';
import { VehicleListComponent } from './components/user-vehicle/component/user-vehicle-list/user-vehicle-list.component';
import { UserVehicleComponent } from './components/user-vehicle/component/user-vehicle/user-vehicle.component';
import { UdesViewComponentComponent } from './components/user-vehicle/component/udes-view-component/udes-view-component.component';
import { UserParkingConfirmModalComponent } from './components/user-parking/confirm-modal/confirm-modal.component';
import { UserParkingComponent } from './components/user-parking/user-parking/user-parking.component';
import { UserCardNotConnectedFormComponent } from './forms/user-card-not-connected-form/user-card-not-connected-form.component';
import { CreateNotConnectedCardDialogComponent } from './dialogs/create-not-connected-card-dialog/create-not-connected-card-dialog.component';
import { StepInfoNotConnectedComponent } from './components/user-cards/steps-not-connected/Info-not-connected/info-not-connected.component';
import { StepConfirmationNotConnectedComponent } from './components/user-cards/steps-not-connected/confirmation-not-connected/confirmation-not-connected.component';
import { UserCardNotConnectedRequestDetailDialogComponent } from './dialogs/user-card-not-connected-request-detail-dialog/user-card-not-connected-request-detail-dialog.component';
import { UserCardRequestApprovalNotNeededDialogComponent } from './dialogs/user-card-request-approval-not-needed-dialog/user-card-request-approval-not-needed-dialog.component';
import { UserHistoryComponent } from './components/user-history/user-history.component';
import { MoreFiltersDialogComponent } from './dialogs/more-filters-dialog/more-filters-dialog.component';

@NgModule({
    declarations: [
        UserRecordsComponent,
        UserTableComponent,
        UserDeleteDialogComponent,
        UserCreateComponent,
        UserUpdateComponent,
        UserConfirmAccountDialogComponent,
        UserCardRequestDetailDialogComponent,
        UserCardDeleteDialogComponent,
        UserProfileComponent,
        UserFamilyAndCardDialogComponent,
        StepMembersComponent,
        StepConfirmComponent,
        StepCardComponent,
        UserCardRequestDetailFormComponent,
        UserFamilyRequestCardComponent,
        UserRequestDenyDialogComponent,
        UserCardDeleteErrorDialogComponent,
        EmailChangedBannerComponent,
        StepConfirmationComponent,
        AddNewFamilyMemberDialogComponent,
        AddressCardComponent,
        EditAddressDialogComponent,
        LinkCardDialogComponent,
        IncompleteUserRecordComponent,
        UpdateCardDialogComponent,
        AddFamilyMemberSelectionDialogComponent,
        AddExistingUserDialogComponent,
        UserCardComponent,
        FamilyCardComponent,
        FamilyMemberButtonComponent,
        UserCardSelectComponent,
        UserCardSelectDropdownComponent,
        StepInfoComponent,
        CardComponent,
        UserSearchResultsDialog,
        CreateCardDialogComponent,
        UserCardConfirmDialogComponent,
        IncompleteProfileComponent,
        BcitiStepInfoComponent,
        UserFamilyComponent,
        FamilyMemberComponent,
        UserFamilySelectComponent,
        UserFamilySelectDropdownComponent,
        AddFamilyMemberDialogComponent,
        EditFamilyLinkDialogComponent,
        RemoveFamilyConfirmDialogComponent,
        ForceAcceptFamilyLinkDialogComponent,
        SafeHtmlPipe,
        UserSubscriptionsComponent,
        SubscriptionComponent,
        SubscriptionManagementDialogComponent,
        SubscriptionPaymentDialogComponent,
        UserReceiptDetailDialogComponent,
        UserReceiptSummaryComponent,
        UserReceiptsComponent,
        UserRequestComponent,
        UserRequestDetailComponent,
        UserCardProfilePictureComponent,
        VehiculeModalComponent,
        VehicleListComponent,
        UserVehicleComponent,
        UdesViewComponentComponent,
        UserCardNotConnectedFormComponent,
        UserParkingConfirmModalComponent,
        UserParkingComponent,
        CreateNotConnectedCardDialogComponent,
        StepInfoNotConnectedComponent,
        StepConfirmationNotConnectedComponent,
        UserCardNotConnectedRequestDetailDialogComponent,
        UserCardRequestApprovalNotNeededDialogComponent,
        UserHistoryComponent,
        MoreFiltersDialogComponent,
    ],
    providers: [USER_SERVICE_PROVIDER, GEOID_SERVICE_PROVIDER, PermissionGuard, ScopeGuard],
    imports: [
        AppRoutingModule,
        BrowserModule,
        AngularCommonModule,
        MatDialogModule,
        FormsModule,
        MatMenuModule,
        ReactiveFormsModule,
        TranslateModule,
        GlobalModule,
        MaterialModule,
        UploadModalModule,
        FileAttachmentModule,
        NgbModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatButtonModule,
    ],
    exports: [TranslateModule],
})
export class UserModule {}
